import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Header } from "../../components/SpearParts/Layouts/Header";
import { SeachSection } from "../../components/SpearParts/Layouts/SeachSection";
import Advancedfilter from "../AdvancedFilter/Advancedfilter";
import Axios from "../../axios/axios";
import { Footer } from "../../components/SpearParts/Layouts/Footer";
import { toTitleCase } from "../../components/utils/StringUtil";
import NoData from "../../components/NoData/Index";
import "./product.css";
import Middle from "../../components/SpearParts/Layouts/Middle";
import TopNavBar from "../../components/SpearParts/Layouts/TopNavBar";
import TopSearchBar from "../../components/SpearParts/Layouts/TopSearchBar";
import { resolveEntityName, getProductName, getProductPartNo, getProductYear } from "../../components/utils/EntityResolver";

export default function ProductList() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const initialUrlFilters = {
    categoryId: searchParams.get("categoryId") || "",
    subcategoryId: searchParams.get("subcategoryId") || "",
    brandId: searchParams.get("brandId") || "",
    modelId: searchParams.get("modelId") || "",
    regionId: searchParams.get("regionId") || "",
    year: searchParams.get("year") || "",
  };

  const {
    parts = [],
    spareBrand = [],
    spareCategory = [],
    spareModels = [],
    region = [],
    subCategories = [],
    filters: locationStateFilters = {},
  } = location.state || {};

  const [filters, setFilters] = useState({
    ...initialUrlFilters,
    ...locationStateFilters,
  });
  const [filteredParts, setFilteredParts] = useState(parts);
  const [originalSearchResults, setOriginalSearchResults] = useState(parts);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [spareBrandList, setSpareBrandList] = useState(spareBrand);
  const [spareCategoryList, setSpareCategoryList] = useState(spareCategory);
  const [spareModelsList, setSpareModelsList] = useState(spareModels);
  const [regionList, setRegionList] = useState(region);

  useEffect(() => {
    const fetchMasterData = async () => {
      try {
        if (!spareBrandList.length) {
          const res = await Axios.get("/brand");
          if (res.data) setSpareBrandList(Array.isArray(res.data) ? res.data : res.data.data || []);
        }
        if (!spareCategoryList.length) {
          const res = await Axios.get("/categories");
          if (res.data) setSpareCategoryList(Array.isArray(res.data) ? res.data : res.data.data || []);
        }
        if (!spareModelsList.length) {
          const res = await Axios.get("/model");
          if (res.data) setSpareModelsList(Array.isArray(res.data) ? res.data : res.data.data || []);
        }
        if (!regionList.length) {
          const res = await Axios.get("/regions");
          if (res.data) setRegionList(Array.isArray(res.data) ? res.data : res.data.data || []);
        }
      } catch (err) {
        console.error("Error fetching master data in ProductList:", err);
      }
    };
    fetchMasterData();
  }, []);

  const getBrandName = (brandId) => resolveEntityName(brandId, spareBrandList, "Unknown Brand");
  const getModelName = (modelId) => resolveEntityName(modelId, spareModelsList, "Unknown Model");
  const getCategoryName = (categoryId, part) =>
    resolveEntityName(categoryId || part?.subcategoryId || part?.category, spareCategoryList, "Unknown Category");
  const getRegionName = (regionId) => resolveEntityName(regionId, regionList, "Unknown Region");

  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  useEffect(() => {
    const fetchParts = async () => {
      try {
        const searchParams = new URLSearchParams(location.search);
        const urlParams = {
          categoryId: searchParams.get("categoryId") || undefined,
          subcategoryId: searchParams.get("subcategoryId") || undefined,
          brandId: searchParams.get("brandId") || undefined,
          modelId: searchParams.get("modelId") || undefined,
          regionId: searchParams.get("regionId") || undefined,
          year: searchParams.get("year") || undefined,
        };

        const activeFilters = {};
        Object.keys({ ...urlParams, ...filters }).forEach((key) => {
          const val = filters[key] !== undefined && filters[key] !== "" ? filters[key] : urlParams[key];
          if (val !== undefined && val !== null && val !== "") {
            activeFilters[key] = val;
          }
        });

        const res = await Axios.get("/search/categories", {
          params: {
            ...activeFilters,
            page: currentPage,
            limit: 15,
          },
        });

        const partsData = Array.isArray(res.data?.parts)
          ? res.data.parts
          : Array.isArray(res.data)
          ? res.data
          : [];

        setFilteredParts(partsData);
        setOriginalSearchResults(partsData);
        setTotalPages(res.data?.totalPages || 1);
      } catch (err) {
        console.error("Error loading parts:", err);
        setFilteredParts([]);
        setOriginalSearchResults([]);
        setTotalPages(1);
      }
    };

    fetchParts();
  }, [location.search, currentPage, filters]);

  const navigate = useNavigate();

  const clearAllFilters = () => {
    setFilters({
      categoryId: "",
      subcategoryId: "",
      subCategoryId: "",
      brandId: "",
      modelId: "",
      regionId: "",
      year: "",
      steering: "",
      bodyType: "",
      transmission: "",
      transmissionType: "",
      modification: "",
      engine: "",
      engineCode: "",
      grade: "",
    });
    navigate(location.pathname, { replace: true });
  };

  const removeSingleFilter = (keyToRemove) => {
    const updatedFilters = { ...filters };
    delete updatedFilters[keyToRemove];
    if (keyToRemove === "subcategoryId") delete updatedFilters["subCategoryId"];
    if (keyToRemove === "subCategoryId") delete updatedFilters["subcategoryId"];
    if (keyToRemove === "transmission") delete updatedFilters["transmissionType"];
    if (keyToRemove === "transmissionType") delete updatedFilters["transmission"];
    setFilters(updatedFilters);

    const searchParams = new URLSearchParams(location.search);
    searchParams.delete(keyToRemove);
    if (keyToRemove === "subcategoryId") searchParams.delete("subCategoryId");
    if (keyToRemove === "subCategoryId") searchParams.delete("subcategoryId");
    if (keyToRemove === "transmission") searchParams.delete("transmissionType");
    if (keyToRemove === "transmissionType") searchParams.delete("transmission");
    const newSearch = searchParams.toString();
    navigate(newSearch ? `${location.pathname}?${newSearch}` : location.pathname, { replace: true });
  };

  const getFilterLabel = (key, val) => {
    if (!val) return null;
    if (key === "categoryId") return `Category: ${getCategoryName(val, null)}`;
    if (key === "subcategoryId" || key === "subCategoryId") return `Subcategory: ${resolveEntityName(val, subCategories, val)}`;
    if (key === "brandId") return `Brand: ${getBrandName(val)}`;
    if (key === "modelId") return `Model: ${getModelName(val)}`;
    if (key === "regionId") return `Region: ${getRegionName(val)}`;
    if (key === "year") return `Year: ${val}`;
    if (key === "steering") return `Steering: ${toTitleCase(val)}`;
    if (key === "bodyType") return `Body: ${toTitleCase(val)}`;
    if (key === "transmission" || key === "transmissionType") return `Transmission: ${toTitleCase(val)}`;
    if (key === "engine") return `Engine: ${toTitleCase(val)}`;
    if (key === "engineCode") return `Engine Code: ${val}`;
    if (key === "grade") return `Grade: ${toTitleCase(val)}`;
    if (key === "modification") return `Modification: ${val}`;
    return `${toTitleCase(key)}: ${val}`;
  };

  const urlSearchParamsObj = Object.fromEntries(new URLSearchParams(location.search).entries());
  const combinedActiveFilters = { ...urlSearchParamsObj, ...filters };
  delete combinedActiveFilters["page"];
  delete combinedActiveFilters["limit"];

  const activeFilterEntries = Object.entries(combinedActiveFilters)
    .filter(([k, v]) => v !== undefined && v !== null && v !== "" && k !== "subCategoryId" && k !== "transmissionType")
    .map(([key, val]) => ({ key, label: getFilterLabel(key, val) }))
    .filter(item => item.label);

  const hasActiveFilters = activeFilterEntries.length > 0;

  return (
    <>
      <div>
        <section id="banner">
          <div className="Main-div">
            <TopNavBar />
            <div className="TopSearchBar">
              <TopSearchBar />
            </div>
            <div className="container">
              <div className="bnr-div">
                <Middle initialFilters={filters} />
              </div>

              <div className="BuildingProductList">
                {/* Advanced Filter on left */}
                <div className="col-lg-3">
                  <div className="advanced-filter-card p-3 mt-5">
                    <Advancedfilter
                      categories={spareCategoryList}
                      subCategories={subCategories}
                      setParentFilters={setFilters}
                      filters={filters}
                    />
                  </div>
                </div>

                {/* Parts list / Search results */}
                <div className="col-lg-9">
                  {hasActiveFilters && (
                    <div className="active-filters-container mb-3 p-3 bg-light border rounded d-flex flex-wrap align-items-center justify-content-between gap-2">
                      <div className="d-flex flex-wrap align-items-center gap-2">
                        <span className="fw-bold text-dark me-2">Active Filters:</span>
                        {activeFilterEntries.map(({ key, label }) => (
                          <span
                            key={key}
                            className="badge bg-primary text-white d-inline-flex align-items-center gap-2 px-3 py-2"
                            style={{ fontSize: "13px", fontWeight: "500", borderRadius: "20px" }}
                          >
                            {label}
                            <span
                              style={{ cursor: "pointer", fontWeight: "bold", marginLeft: "4px" }}
                              onClick={() => removeSingleFilter(key)}
                              title="Remove filter"
                            >
                              ✕
                            </span>
                          </span>
                        ))}
                      </div>
                      <button
                        className="btn btn-sm btn-outline-danger px-3 py-1 fw-bold"
                        onClick={clearAllFilters}
                        style={{ borderRadius: "20px" }}
                      >
                        ✕ Clear All Filters
                      </button>
                    </div>
                  )}
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h6 className="m-0">
                      Search results ({filteredParts ? filteredParts.length : 0})
                    </h6>

                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => setFilteredParts(originalSearchResults)}
                      >
                        All
                      </button>

                      <button
                        className="btn btn-sm btn-outline-success"
                        onClick={() => {
                          const genuineParts = originalSearchResults.filter(
                            (p) => p.partQuality?.toLowerCase() === "genuine"
                          );
                          setFilteredParts(genuineParts);
                        }}
                      >
                        Genuine
                      </button>

                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => {
                          const aftermarketParts = originalSearchResults.filter(
                            (p) => p.partQuality?.toLowerCase() === "aftermarket"
                          );
                          setFilteredParts(aftermarketParts);
                        }}
                      >
                        Aftermarket
                      </button>
                    </div>
                  </div>

                  <div className="table-responsive">
                    <table className="table search-table">
                      <tbody>
                        <tr>
                          <td colSpan="8" className="p-0">
                            <div className="product-header-row">
                              <div className="head-cell name">Name</div>
                              <div className="head-cell">Part No</div>
                              <div className="head-cell">Brand</div>
                              <div className="head-cell">Model</div>
                              <div className="head-cell">Category</div>
                              <div className="head-cell">Region</div>
                              <div className="head-cell">Year</div>
                              <div className="head-cell icon"></div>
                            </div>
                          </td>
                        </tr>

                        <tr>
                          <td colSpan="8" className="border-td p-0">
                            <table className="table search-inner-table">
                              <tbody>
                                {filteredParts && filteredParts.length > 0 ? (
                                  filteredParts.map((part, index) => (
                                    <tr
                                      key={index}
                                      className="product-row-card"
                                    >
                                      <td colSpan="8">
                                        <div className="product-row">
                                          <div className="cell name">
                                            <Link
                                              to={`/singleproduct/${part._id}`}
                                              state={{
                                                parts,
                                                spareCategory,
                                                spareBrand,
                                                spareModels,
                                                region,
                                              }}
                                            >
                                              {getProductName(part)}
                                            </Link>
                                          </div>
                                          <div className="cell">
                                            {getProductPartNo(part)}
                                          </div>
                                          <div className="cell">
                                            {toTitleCase(
                                              getBrandName(part?.brandId)
                                            )}
                                          </div>
                                          <div className="cell">
                                            {toTitleCase(
                                              getModelName(part?.modelId)
                                            )}
                                          </div>
                                          <div className="cell">
                                            {toTitleCase(
                                              getCategoryName(part?.categoryId, part)
                                            )}
                                          </div>
                                          <div className="cell">
                                            {toTitleCase(
                                              getRegionName(part?.regionId)
                                            )}
                                          </div>
                                          <div className="cell">
                                            {getProductYear(part)}
                                          </div>

                                          <div className="cell icon">
                                            <Link
                                              to={`/singleproduct/${part._id}`}
                                              state={{
                                                parts,
                                                spareCategory,
                                                spareBrand,
                                                spareModels,
                                                region,
                                              }}
                                            >
                                              <img src="images/go.png" alt="" />
                                            </Link>
                                          </div>
                                        </div>
                                      </td>
                                    </tr>
                                  ))
                                ) : (
                                  <tr>
                                    <td
                                      colSpan="8"
                                      className="text-center"
                                    >
                                      <NoData module="Spare Parts" />
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    <div className="pagination-container d-flex justify-content-end align-items-center mt-4">
                      <button
                        className="pagination-btn"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(currentPage - 1)}
                      >
                        <i className="fa fa-arrow-left"></i>
                      </button>

                      <span className="mx-3">
                        {currentPage} of {totalPages}
                      </span>

                      <button
                        className="pagination-btn"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(currentPage + 1)}
                      >
                        <i className="fa fa-arrow-right"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
